import { Box, Fade, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Container, Section, Text } from '../components/atomic';
import SEO from '../components/SEO';
import SecureEmail from '../components/SecureEmail';
import { LegalPageLayout } from '../layouts';

/**
 * Privacy Policy page component for EllaFin
 *
 * Dark navy aesthetic matching the Homepage/About/Product pages:
 * - Dark navy hero section with white text
 * - Dark content section with readable light typography
 * - Accent green for list markers and section headings
 * - Clean, readable typography optimized for legal content
 * - Print-friendly layout
 * - Proper heading hierarchy (h2 for main sections, h3 for subsections)
 *
 * @returns {JSX.Element} Privacy Policy page component
 */
function PrivacyPolicy() {
  const [isLoaded, setIsLoaded] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  /**
   * Common styles for legal document sections
   */
  const sectionStyles = {
    mb: 5,
  };

  const sectionHeadingStyles = {
    fontSize: { xs: '1.25rem', md: '1.5rem' },
    fontWeight: 700,
    color: '#ffffff !important',
    mb: 2,
    textAlign: 'left',
    fontFamily: '"Inter", "Helvetica", sans-serif',
  };

  const paragraphStyles = {
    lineHeight: 1.8,
    color: alpha('#ffffff', 0.8),
    mb: 2,
    fontSize: { xs: '1rem', md: '1.1rem' },
  };

  const listStyles = {
    pl: { xs: 3, md: 4 },
    m: 0,
    mb: 2,
  };

  const listItemStyles = {
    mb: 1.5,
    color: alpha('#ffffff', 0.8),
    lineHeight: 1.7,
    fontSize: { xs: '1rem', md: '1.1rem' },
    '&::marker': { color: theme.palette.secondary.main },
  };

  return (
    <LegalPageLayout>
      <SEO
        title="Privacy Policy"
        description="EllaFin Privacy Policy governing data collection for the FinOps Business Intelligence Platform. Covers CCPA/CPRA rights, personal information use, third-party sharing, and opt-out options."
        path="/privacy"
        noIndex={false}
        keywords="privacy policy, data protection, CCPA, CPRA, personal information"
      />

      {/* Hero Section */}
      <Box
        component="section"
        aria-label="Privacy Policy hero"
        sx={{
          position: 'relative',
          py: { xs: 8, md: 12 },
          overflow: 'hidden',
          backgroundColor: '#1e2140',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Fade in={isLoaded} timeout={600}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: theme.palette.secondary.main,
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    fontSize: '0.875rem',
                    mb: 2,
                    display: 'block',
                  }}
                >
                  Legal
                </Typography>
              </Box>
            </Fade>

            <Fade in={isLoaded} timeout={800}>
              <Box>
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    mb: 3,
                    color: '#ffffff',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Privacy Policy
                </Typography>
              </Box>
            </Fade>

            <Fade in={isLoaded} timeout={1000}>
              <Box>
                <Text
                  sx={{
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    color: alpha('#ffffff', 0.7),
                    lineHeight: 1.7,
                    maxWidth: '700px',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  This Privacy Policy governs data collection and usage for
                  EllaFin's FinOps Business Intelligence Platform. By using the
                  Company application, you consent to the data practices
                  described in this statement.
                </Text>
                <Text
                  sx={{
                    fontSize: '0.9rem',
                    color: alpha('#ffffff', 0.5),
                    fontStyle: 'italic',
                    textAlign: 'center',
                    display: 'block',
                  }}
                >
                  Effective as of September 04, 2025
                </Text>
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>

      {/* Legal Document Content */}
      <Section
        sx={{
          backgroundColor: '#13152b',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="md">
          <Box
            component="article"
            sx={{
              px: { xs: 2, md: 0 },
            }}
          >
            {/* Section 1 - CCPA/CPRA */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                1. California Consumer Privacy Act (CCPA/CPRA)
              </Typography>
              <Text sx={paragraphStyles}>
                If you are a California resident, you have the following rights
                under the California Consumer Privacy Act and California Privacy
                Rights Act:
              </Text>
              <Box component="ul" sx={listStyles}>
                <Box component="li" sx={listItemStyles}>
                  <strong>Right to Know.</strong> You may request details on
                  what personal data we collect, use, and share.
                </Box>
                <Box component="li" sx={listItemStyles}>
                  <strong>Right to Delete.</strong> You can request deletion of
                  personal data, subject to certain legal exceptions.
                </Box>
                <Box component="li" sx={listItemStyles}>
                  <strong>Right to Correct.</strong> You may request corrections
                  to inaccurate personal information.
                </Box>
                <Box component="li" sx={listItemStyles}>
                  <strong>Right to Opt-Out.</strong> You can opt out of the sale
                  or sharing of personal data for advertising.
                </Box>
                <Box component="li" sx={listItemStyles}>
                  <strong>Right to Restrict Sensitive Data Use.</strong> You may
                  limit the use of sensitive personal information.
                </Box>
                <Box component="li" sx={listItemStyles}>
                  <strong>Right Against Retaliation.</strong> The Company will
                  not discriminate against you for exercising your rights.
                </Box>
              </Box>
            </Box>

            {/* Section 2 - Collection of Personal Information */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                2. Collection of Personal Information
              </Typography>
              <Text sx={paragraphStyles}>
                In order to better provide you with products and services
                offered, the Company may collect personally identifiable
                information, such as:
              </Text>
              <Box component="ul" sx={listStyles}>
                <Box component="li" sx={listItemStyles}>
                  First and last name
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Email address
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Phone number
                </Box>
              </Box>
              <Text sx={paragraphStyles}>
                If you purchase the Company's products and services, we collect
                billing and credit card information. This information is used to
                complete the purchase transaction.
              </Text>
              <Text sx={paragraphStyles}>
                We do not collect any personal information about you unless you
                voluntarily provide it to us. However, you may be required to
                provide certain personal information when you elect to use
                certain products or services. These may include: (a) registering
                for an account; (b) entering a sweepstakes or contest sponsored
                by us or one of our partners; (c) signing up for special offers
                from selected third parties; (d) sending us an email message;
                (e) submitting your credit card or other payment information
                when ordering and purchasing products and services.
              </Text>
            </Box>

            {/* Section 3 - Use of Personal Information */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                3. Use of Personal Information
              </Typography>
              <Text sx={paragraphStyles}>
                The Company collects and uses your personal information in the
                following ways:
              </Text>
              <Box component="ul" sx={listStyles}>
                <Box component="li" sx={listItemStyles}>
                  To operate and deliver the services you have requested;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  To provide you with information, products, or services that
                  you request from us;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  To provide you with notices about your account;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  To carry out the Company's obligations and enforce our rights
                  arising from any contracts entered between you and us;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  To notify you about changes to EllaFin or any products or
                  services we offer;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  In any other way we may describe when you provide the
                  information;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  For any other purpose with your consent.
                </Box>
              </Box>
            </Box>

            {/* Section 4 - Sharing Information with Third Parties */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                4. Sharing Information with Third Parties
              </Typography>
              <Text sx={paragraphStyles}>
                The Company does not sell, rent, or lease its customer lists to
                third parties.
              </Text>
              <Text sx={paragraphStyles}>
                The Company may, from time to time, contact you on behalf of
                external business partners about a particular offering that may
                be of interest to you. In those cases, your unique personally
                identifiable information (email, name, address, phone number) is
                not transferred to the third party. The Company may share data
                with trusted partners to help perform statistical analysis, send
                you email or postal mail, provide customer support, or arrange
                for deliveries.
              </Text>
              <Text sx={paragraphStyles}>
                All such third parties are prohibited from using your personal
                information except to provide these services to the Company, and
                they are required to maintain the confidentiality of your
                information.
              </Text>
              <Text sx={paragraphStyles}>
                The Company may disclose your personal information, without
                notice, if required to do so by law or in the good faith belief
                that such action is necessary to: (a) conform to the edicts of
                the law or comply with legal process served on the Company or
                the site; (b) protect and defend the rights or property of the
                Company; and/or (c) act under exigent circumstances to protect
                the personal safety of users of the Company, or the public.
              </Text>
            </Box>

            {/* Section 5 - Opt-Out of Disclosure */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                5. Opt-Out of Disclosure
              </Typography>
              <Text sx={paragraphStyles}>
                In connection with any personal information we may disclose to a
                third party for a business purpose, you have the right to know
                the categories of personal information that we disclosed about
                you for a business purpose.
              </Text>
              <Text sx={paragraphStyles}>
                You have the right under the CCPA and CPRA and certain other
                privacy and data protection laws, as applicable, to opt out of
                the disclosure of your personal information. If you exercise
                your right to opt out of the disclosure of your personal
                information, we will refrain from disclosing your personal
                information, unless you subsequently provide express
                authorization for the disclosure of your personal information.
                To opt out of the disclosure of your personal information,
                please contact us using the information provided below.
              </Text>
            </Box>

            {/* Section 6 - Right to Deletion */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                6. Right to Deletion
              </Typography>
              <Text sx={paragraphStyles}>
                Subject to certain exceptions set out below, on receipt of a
                verifiable request from you, we will delete your personal
                information from our records and direct any service providers to
                delete your personal information from their records.
              </Text>
              <Text sx={paragraphStyles}>
                Under the CCPA and CPRA, you have the right to request that the
                Company delete any personal information that has been collected
                about you. To exercise your rights, contact us at{' '}
                <SecureEmail
                  emailKey="support"
                  showAsLink={false}
                  component="span"
                  sx={{ color: theme.palette.secondary.main }}
                />
                .
              </Text>
              <Text sx={paragraphStyles}>
                Please note that we may not be able to comply with requests to
                delete your personal information if it is necessary to:
              </Text>
              <Box component="ul" sx={listStyles}>
                <Box component="li" sx={listItemStyles}>
                  Complete the transaction for which the personal information
                  was collected, fulfill the terms of a written warranty or
                  product recall conducted in accordance with federal law,
                  provide a good or service requested by you, or reasonably
                  anticipated within the context of our ongoing business
                  relationship with you, or otherwise perform a contract between
                  you and us;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Detect security incidents, protect against malicious,
                  deceptive, fraudulent, or illegal activity; or prosecute those
                  responsible for that activity;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Debug to identify and repair errors that impair existing
                  intended functionality;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Exercise free speech, ensure the right of another consumer to
                  exercise his or her right of free speech, or exercise another
                  right provided for by law;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Comply with the California Electronic Communications Privacy
                  Act;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Engage in public or peer-reviewed scientific, historical, or
                  statistical research in the public interest that adheres to
                  all other applicable ethics and privacy laws, when our
                  deletion of the information is likely to render impossible or
                  seriously impair the achievement of such research, provided we
                  have obtained your informed consent;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Enable solely internal uses that are reasonably aligned with
                  your expectations based on your relationship with us;
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Comply with an existing legal obligation; or
                </Box>
                <Box component="li" sx={listItemStyles}>
                  Otherwise use your personal information, internally, in a
                  lawful manner that is compatible with the context in which you
                  provided the information.
                </Box>
              </Box>
            </Box>

            {/* Section 7 - Children Under Thirteen */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                7. Children Under Thirteen
              </Typography>
              <Text sx={paragraphStyles}>
                The Company does not knowingly collect personally identifiable
                information from children under the age of 13. If you are under
                the age of 13, you must ask your parent or guardian for
                permission to use this application.
              </Text>
            </Box>

            {/* Section 8 - Email Communications */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                8. Email Communications
              </Typography>
              <Text sx={paragraphStyles}>
                From time to time, the Company may contact you via email for the
                purpose of providing announcements, promotional offers, alerts,
                confirmations, surveys, and/or other general communication. In
                order to improve our services, we may receive a notification
                when you open an email from the Company or click on a link
                therein.
              </Text>
              <Text sx={paragraphStyles}>
                If you would like to stop receiving marketing or promotional
                communications via email from the Company, you may opt out of
                such communications by replying STOP or clicking on the
                unsubscribe button.
              </Text>
            </Box>

            {/* Section 9 - External Data Storage */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                9. External Data Storage
              </Typography>
              <Text sx={paragraphStyles}>
                We may store your data on servers provided by third-party
                hosting vendors with whom we have contracted. All hosting
                providers are required to maintain appropriate security measures
                and comply with our data protection requirements.
              </Text>
            </Box>

            {/* Section 10 - Changes to This Statement */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                10. Changes to This Statement
              </Typography>
              <Text sx={paragraphStyles}>
                The Company reserves the right to change this Policy from time
                to time. When changes to this Policy are significant, we will
                inform you by posting the amended Policy on our website, sending
                email notification, or by other means. Your continued use of the
                application and/or services available after such modifications
                will constitute your acknowledgment of the modified Policy and
                agreement to abide and be bound by that Policy.
              </Text>
            </Box>

            {/* Section 11 - Third-Party Opt-Out */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                11. Third-Party Opt-Out
              </Typography>
              <Text sx={paragraphStyles}>
                We respect your privacy and give you an opportunity to opt out
                of receiving announcements of certain information. Users may opt
                out of receiving any or all communications from third-party
                partners of the Company by contacting us at{' '}
                <SecureEmail
                  emailKey="support"
                  showAsLink={false}
                  component="span"
                  sx={{ color: theme.palette.secondary.main }}
                />
                .
              </Text>
            </Box>

            {/* Section 12 - Contact Information */}
            <Box component="section" sx={sectionStyles}>
              <Typography variant="h2" component="h2" sx={sectionHeadingStyles}>
                12. Contact Information
              </Typography>
              <Text sx={paragraphStyles}>
                The Company welcomes your questions or comments regarding this
                Policy. If you believe that the Company has not adhered to this
                Policy, please contact us at:
              </Text>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  backgroundColor: alpha('#ffffff', 0.04),
                  borderLeft: `4px solid ${theme.palette.secondary.main}`,
                  mt: 3,
                }}
              >
                <Text
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: '#ffffff',
                    fontSize: '1.1rem',
                  }}
                >
                  EllaFin
                </Text>
                <Text sx={{ lineHeight: 1.8, color: alpha('#ffffff', 0.7) }}>
                  401 W A Street Ste. 200
                  <br />
                  San Diego, California 92101
                  <br />
                  <br />
                  Email:{' '}
                  <SecureEmail
                    emailKey="support"
                    showAsLink={false}
                    component="span"
                    sx={{ color: theme.palette.secondary.main }}
                  />
                </Text>
              </Box>
            </Box>
          </Box>
        </Container>
      </Section>
    </LegalPageLayout>
  );
}

export default PrivacyPolicy;
